"use client";

import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cn } from "@/lib/utils";
import { CheckCircle2, XCircle, Info, AlertTriangle } from "lucide-react";

//Toast Provider
const ToastProvider = ToastPrimitives.Provider;

//Top-right stacked position
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-6 right-6 z-[100] flex flex-col gap-3 w-full max-w-sm outline-none",
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

// Variant colors (SaaS clean style)
const variantStyles: Record<string, string> = {
  success:
    "border-green-500 bg-green-50 text-green-900 dark:bg-green-900/30 dark:text-green-100",
  destructive:
    "border-red-500 bg-red-50 text-red-900 dark:bg-red-900/30 dark:text-red-100",
  warning:
    "border-yellow-500 bg-yellow-50 text-yellow-900 dark:bg-yellow-900/30 dark:text-yellow-100",
  info: "border-blue-500 bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100",
  default:
    "border-border bg-background text-foreground dark:bg-neutral-900 dark:text-neutral-100",
};

// Main Toast Component
const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & {
    variant?: keyof typeof variantStyles;
  }
>(({ className, variant = "default", children, ...props }, ref) => (
  <ToastPrimitives.Root
    ref={ref}
    className={cn(
      "group relative flex w-full items-start gap-3 overflow-hidden rounded-lg border p-4 shadow-lg backdrop-blur-sm transition-all",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=open]:fade-in-80 data-[state=closed]:fade-out-80",
      "data-[state=open]:slide-in-from-right-full sm:data-[state=open]:slide-in-from-top-full",
      variantStyles[variant],
      className
    )}
    {...props}
  >
    {/* Icons per variant */}
    {variant === "success" && (
      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
    )}
    {variant === "destructive" && (
      <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
    )}
    {variant === "warning" && (
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
    )}
    {variant === "info" && (
      <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
    )}

    {/* Content */}
    <div className="flex-1 flex flex-col">{children}</div>

    {/* Close button */}
    <ToastClose className="absolute right-3 top-3 opacity-70 hover:opacity-100 text-sm">
      ✕
    </ToastClose>
  </ToastPrimitives.Root>
));
Toast.displayName = ToastPrimitives.Root.displayName;

// Title
const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      "text-sm font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

// Description
const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground mt-1", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

// Close primitive
const ToastClose = ToastPrimitives.Close;

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
};
